import { Component, computed, inject, input } from "@angular/core";

import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import {
  BudgetAmounts,
  BudgetCategory,
  BudgetIntervalType,
} from "@bitwarden/web-vault/app/models/types/budget.types";
import { BudgetHistoryTable } from "@bitwarden/web-vault/app/components/tables/budget-tables/budget-history-table/budget-history.table";
import { BudgetTable } from "@bitwarden/web-vault/app/components/tables/budget-tables/budget.table";
import { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import {
  observeBreakpoint,
  observeSmallScreen,
  XL_BREAKPOINT,
} from "@bitwarden/web-vault/app/shared/breakpoints";
import { BudgetClockService } from "@bitwarden/web-vault/app/gloss/budget/budget-clock.service";
import { format } from "date-fns";

@Component({
  selector: "app-budget-details",
  templateUrl: "./budget-details.component.html",
})
export class BudgetDetailsComponent extends OnBackDirective {
  private userStore = inject(UserStoreService);
  private budgetStoreService = inject(BudgetStoreService);
  private confirmationService = inject(ConfirmationDialogService);
  /** Local-day signal the interval-derived computeds depend on, so they refresh across an interval reset (see now()). */
  private clock = inject(BudgetClockService);

  /** True when viewport ≤ 639 px or the device UA-detects as mobile — switches details-wrapper
   *  to mobile (back-button) layout. Must match details-wrapper's own showMobileLayout check
   *  (see observeSmallScreen doc) or the parent page and the nested wrapper can disagree. */
  protected isSmallScreen = observeSmallScreen();

  /** True when viewport ≤ 1279 px (Tailwind's `xl` screen — see tailwind.config.js) — the detail
   *  panel is too narrow for the history table's 4 columns, so historical spendings render as a
   *  stacked single-column list instead. */
  protected isCompactHistory = observeBreakpoint(XL_BREAKPOINT);

  protected dialogFactory: DialogFactory;

  budgetHistoryTable: BudgetHistoryTable = new TablesFactory().get(TablesType.BudgetHistory, this);
  table = input<BudgetTable>();

  coreCurrency = computed(() => this.userStore.preferences.preferenceView()?.coreCurrency || "");

  /** The detail panel is shown in the budget's own currency (falls back to core for legacy budgets). */
  detailCurrency = computed(() => this.latestBudget()?.currency || this.coreCurrency());

  latestBudget = computed(() => {
    const id = this.table()?.budgetToDetail()?.id;
    const views = this.budgetStoreService.budget.budgetViews() ?? [];
    if (!id || views.length === 0) {
      return null;
    }

    return views.find((b) => b.id === id);
  });

  // The detail always reflects the selected budget's own cadence — no page-interval conversion.
  calculationInterval = computed<BudgetIntervalType>(
    () => this.latestBudget()?.interval ?? "monthly",
  );

  // Days-left and resetDate() both come from the calendar-aligned getIntervalResetDate,
  // so they always agree and ignore the creation day / startingPreference.
  remainingDays = computed<number>(() =>
    BudgetUtils.getDaysLeftInInterval(this.calculationInterval(), this.now()),
  );

  progressLabel = computed<string>(() => BudgetUtils.getProgressLabel(this.calculationInterval()));

  /**
   * Current-interval spend computed in the budget's OWN currency. Each transaction (stored
   * core-normalized) is converted back to the budget currency at its own date, so a same-currency
   * spend is exact (¥5000 shows ¥5000) rather than drifting through a single today-based FX factor.
   */
  currentIntervalSpend = computed(() => {
    const budget = this.latestBudget();
    if (!budget) {
      return null;
    }
    const convert = this.budgetStoreService.getBudgetCurrencyConverter(budget);
    return BudgetUtils.calculateCurrentIntervalSpend(
      budget,
      this.budgetStoreService.budgetTransactions.sortedCollection(),
      this.now(),
      budget.amount ?? 0,
      this.calculationInterval(),
      convert,
    );
  });

  // Spent is the magnitude of the category activity for the interval, in the budget's currency.
  currentAmountSpent = computed<number>(() => {
    return this.currentIntervalSpend()?.spent ?? 0;
  });

  budgetCategories = computed<BudgetCategory[]>(() => {
    const budget = this.latestBudget();
    if (!budget) {
      return [];
    }

    return this.populateCategoryData(this.getCurrentCategoryAmounts(budget), budget);
  });

  /** Budgeted amount for the current interval, in the budget's own currency */
  budgetAmount = computed<number>(() => this.currentIntervalSpend()?.budgeted ?? 0);

  /** Remaining amount for the current interval, in the budget's own currency (negative when over budget) */
  remainingAmount = computed<number>(
    () => this.currentIntervalSpend()?.remaining ?? this.currentIntervalSpend()?.budgeted ?? 0,
  );

  /**
   * Spend status band for the current interval (drives progress-bar colour + status text).
   * BudgetUtils.budgetStatus now treats exactly 100% as "over" too, so this agrees with the
   * list view's status for the same budget.
   *
   * Compares the unrounded percentage, not the display-rounded usedPercentage() - rounding
   * first would misclassify real spend within ~0.05% of 100% (e.g. a true 99.96% rounds to
   * 100.0 and would wrongly read as "over").
   */
  progressStatus = computed<"under" | "approaching" | "over">(() =>
    BudgetUtils.budgetStatus(this.exactUsedPercentage()),
  );

  // Aligned on progressStatus's >= 100 boundary rather than remainingAmount() < 0 (its own
  // money-derived sign) - the two disagreed at exactly 100% used, where a same-currency budget
  // has remaining === 0 (not negative) but progressStatus is already "over". Also fixes the
  // budgeted === 0 divergence for free: usedPct short-circuits to 0 there, so progressStatus is
  // "under".
  isOverBudget = computed<boolean>(() => this.progressStatus() === "over");

  // Epsilon rather than exact equality: a cent-exact cross-currency budget accumulates to
  // 99.99999999999999 (per-transaction convert in calculateCurrentIntervalSpend), which exact
  // equality against 100 would miss.
  isBudgetReached = computed<boolean>(() => Math.abs(this.exactUsedPercentage() - 100) < 1e-9);

  /** Date the current interval resets — calendar-aligned (1st of next month, next Monday, …) */
  resetDate = computed<Date | null>(() => {
    if (!this.latestBudget()) {
      return null;
    }
    return BudgetUtils.getIntervalResetDate(this.calculationInterval(), this.now());
  });

  resetDateLabel = computed<string>(() => {
    const date = this.resetDate();
    return date ? format(date, "d MMM yyyy") : "";
  });

  /** Fraction of the current interval elapsed so far, as a percentage, for the days-remaining bar */
  daysElapsedPercent = computed<number>(() => {
    const end = this.resetDate()?.getTime();
    const nowMs = this.now();
    const start = BudgetUtils.getIntervalStart(this.calculationInterval(), 1, nowMs);
    if (!end || end <= start) {
      return 0;
    }
    return Math.min(Math.max(((nowMs - start) / (end - start)) * 100, 0), 100);
  });

  constructor() {
    super();
    this.dialogFactory = new DialogFactory();
  }

  /**
   * Per-category badge. Categories have no individual budget allocation, so when the budget as a
   * whole is over, its categories are flagged "over" (matches the reference design). No badge is
   * shown while the budget is within its limit.
   */
  categoryBadge(): "over" | null {
    return this.isOverBudget() ? "over" : null;
  }

  editBudget(): void {
    this.dialogFactory.editBudget(this.latestBudget());
  }

  async deleteBudget(): Promise<void> {
    const confirmed = await this.confirmationService.confirmFor(ConfirmationEnum.deletingBudget);
    if (!confirmed) {
      return;
    }

    this.budgetStoreService.selectBudget(null);
    this.budgetStoreService.budget.delete(this.table()?.budgetToDetail());
    this.table()?.budgetToDetail.set(null);
    this.confirmationService.notify("success", "succeeded", "budgetDeletedSuccess");
  }

  /**
   * True percentage used for the current interval (uncapped; can exceed 100), unrounded. Used for
   * every threshold decision (progressStatus, the "Budget reached" check) so a spend that's a
   * hair off 100% due to currency conversion (each transaction is converted independently, see
   * currentIntervalSpend above) is classified correctly - only the displayed number (usedPercentage)
   * is rounded, so display and classification can't disagree at the edge in the other direction.
   */
  exactUsedPercentage = computed<number>(() => {
    const budgetAmount = this.budgetAmount();
    if (!budgetAmount) {
      return 0;
    }
    return (this.currentAmountSpent() / budgetAmount) * 100;
  });

  /** Display-rounded version of exactUsedPercentage(); never used for threshold comparisons. */
  usedPercentage = computed<number>(() => Math.round(this.exactUsedPercentage() * 10) / 10);

  /**
   * Width of the white "empty" (spent) fill against the status-coloured background — grows with
   * spend and caps at 100% once the budget is fully used; no special over-budget handling needed,
   * it just stays full.
   */
  progressBarEmptyWidth(): number {
    return Math.min(this.exactUsedPercentage(), 100);
  }

  statusBarClass(status: "under" | "approaching" | "over"): string {
    return BudgetUtils.statusBarClass(status);
  }

  varianceTextClass(variance: number): string {
    return BudgetUtils.varianceTextClass(variance);
  }

  varianceTagClass(variance: number): string {
    return BudgetUtils.varianceTagClass(variance);
  }

  /** Matches the ag-grid history table's own currency formatting so the compact-list fallback
   *  below `xl` renders the same figures identically instead of drifting into a different format. */
  formatCurrency(amount: number | null): string {
    return BudgetUtils.formatCurrency(amount, this.detailCurrency());
  }

  protected handleBackClick(): void {
    this.budgetStoreService.selectBudget(null);
    this.table()?.budgetToDetail.set(null);
  }

  absAmount(amount: number): number;
  absAmount(amount: number | null): number | null;
  absAmount(amount: number | null): number | null {
    return amount == null ? null : Math.abs(amount);
  }

  /**
   * A fresh Date.now() for the interval-derived computeds above. Reading the clock's local-day
   * signal first registers it as their dependency (that is all it is read for), so they re-run when
   * the day changes (an interval reset) yet always measure at the real current time.
   */
  private now(): number {
    this.clock.day();
    return Date.now();
  }

  private getCurrentCategoryAmounts(budget: BudgetView): BudgetAmounts {
    // Reuse the single source of truth (same interval start + filter) instead of re-summing here.
    const spend = this.currentIntervalSpend();
    return {
      budgetAmount: this.budgetAmount(),
      actualAmount: spend?.actual ?? 0,
      varianceAmount: this.remainingAmount(),
      // copy so downstream mutation can't poison the cached currentIntervalSpend signal
      categoryActualAmounts: new Map(
        spend?.categoryActualAmounts ??
          BudgetUtils.getCategoryIdsInBudget(budget).map((id) => [id, 0]),
      ),
    };
  }

  private populateCategoryData(
    currentMonthResult: BudgetAmounts,
    budget: BudgetView,
  ): BudgetCategory[] {
    const categoryEntries = Array.from(currentMonthResult.categoryActualAmounts.entries());

    // Add categories from budget.categoriesLink that are not in categoryActualAmounts
    if (budget?.categoriesLink) {
      for (const categoryLink of budget.categoriesLink) {
        if (!currentMonthResult.categoryActualAmounts.has(categoryLink.id)) {
          categoryEntries.push([categoryLink.id, 0]);
        }
      }
    }

    const categoryViews = this.userStore.categories.categoryViews();

    const categories = categoryEntries.flatMap(([categoryId, amount]) => {
      const category = categoryViews.find((cat) => cat.id === categoryId);
      return category ? [{ category, amount }] : [];
    });

    categories.sort((a, b) => {
      // Sort amount (descending)
      if (a.amount !== b.amount) {
        return a.amount - b.amount;
      }

      // Sort name alphabetically
      const nameA = a.category?.name || "";
      const nameB = b.category?.name || "";
      return nameA.localeCompare(nameB);
    });

    return categories;
  }
}
